import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Download, FileText, Paperclip, Trash2, Upload } from 'lucide-react';
import { toast } from 'react-hot-toast';
import useAuthStore from '../../store/authStore';

const toneByStatus = {
  Missing: 'bg-rose-50 text-rose-700',
  Uploaded: 'bg-blue-50 text-blue-700',
  Verified: 'bg-emerald-50 text-emerald-700',
  Expired: 'bg-amber-50 text-amber-700',
  Optional: 'bg-slate-100 text-slate-500'
};

const DocumentListWidget = ({ category, parentResourceId, title = 'Evraklar' }) => {
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadState, setUploadState] = useState({});
  const [downloadingDocumentId, setDownloadingDocumentId] = useState(null);
  const fileInputRefs = useRef({});
  const canReadDocuments = hasPermission('documents', 'read') || hasPermission('document_definition', 'read');

  const loadMatrix = useCallback(async () => {
    if (!canReadDocuments) {
      setMatrix([]);
      return;
    }

    if (!category || !parentResourceId) return;
    setLoading(true);
    try {
      const { data } = await axios.get('/api/documents/matrix', {
        params: { category, parentResourceId }
      });
      setMatrix(data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Evrak matrisi yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [canReadDocuments, category, parentResourceId]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadMatrix();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [loadMatrix]);

  const handleFileChange = (documentTypeId, fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);

    setUploadState((current) => ({
      ...current,
      [documentTypeId]: {
        ...(current[documentTypeId] || {}),
        files
      }
    }));
  };

  const handleExpiryChange = (documentTypeId, expiresAt) => {
    setUploadState((current) => ({
      ...current,
      [documentTypeId]: {
        ...(current[documentTypeId] || {}),
        expiresAt
      }
    }));
  };

  const openFilePicker = (documentTypeId) => {
    fileInputRefs.current[documentTypeId]?.click();
  };

  const handleDownloadDocument = async (documentId) => {
    if (!documentId) return toast.error('İndirilecek evrak bulunamadı.');
    if (downloadingDocumentId === documentId) return;

    try {
      setDownloadingDocumentId(documentId);
      const { data } = await axios.get(`/api/documents/${documentId}/download-url`);
      const downloadUrl = data?.data?.downloadUrl;
      if (!downloadUrl) throw new Error('İndirme bağlantısı alınamadı.');

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.rel = 'noopener noreferrer';
      link.target = '_self';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Evrak indirilemedi.');
    } finally {
      setDownloadingDocumentId(null);
    }
  };

  const uploadDocument = async (documentTypeId) => {
    const state = uploadState[documentTypeId] || {};
    if (!state.files?.length) return toast.error('Yüklenecek dosyayı seçin.');

    const formData = new FormData();
    formData.append('documentTypeId', documentTypeId);
    formData.append('category', category);
    formData.append('parentResourceId', parentResourceId);
    state.files.forEach((file) => {
      formData.append('files', file);
    });
    if (state.expiresAt) formData.append('expiresAt', state.expiresAt);

    try {
      await axios.post('/api/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Evrak yüklendi.');
      setUploadState((current) => ({ ...current, [documentTypeId]: {} }));
      if (fileInputRefs.current[documentTypeId]) {
        fileInputRefs.current[documentTypeId].value = '';
      }
      await loadMatrix();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Evrak yüklenemedi.');
    }
  };

  const deleteDocument = async (documentId) => {
    try {
      await axios.delete(`/api/documents/${documentId}`);
      toast.success('Evrak silindi.');
      await loadMatrix();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Evrak silinemedi.');
    }
  };

  const renderMatrixRow = (row) => {
    const documentType = row.documentType || {};
    const documentTypeId = documentType._id || row.documentTypeId || row._id;
    const status = row.status || 'Optional';
    const documents = row.documents || (row.document ? [row.document] : []);

    if (!documentTypeId) return null;

    return (
      <div key={documentTypeId} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-black text-slate-800">{documentType.name || 'Tanımsız Evrak Tipi'}</p>
              <span className="rounded-lg bg-slate-200 px-2 py-1 text-[10px] font-black text-slate-700">
                {documents.length} dosya
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className={`rounded-lg px-2 py-1 text-[10px] font-black ${toneByStatus[status] || toneByStatus.Optional}`}>
                {status}
              </span>
              {documentType.isMandatory && <span className="rounded-lg bg-rose-50 px-2 py-1 text-[10px] font-black text-rose-700">Zorunlu</span>}
              {documentType.hasExpiryDate && <span className="rounded-lg bg-amber-50 px-2 py-1 text-[10px] font-black text-amber-700">Süreli</span>}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {row.canUpload && (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={(element) => {
                    if (element) {
                      fileInputRefs.current[documentTypeId] = element;
                    } else {
                      delete fileInputRefs.current[documentTypeId];
                    }
                  }}
                  type="file"
                  multiple
                  accept="application/pdf,image/jpeg,image/png"
                  onChange={(event) => handleFileChange(documentTypeId, event.target.files || [])}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => openFilePicker(documentTypeId)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-100 px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-700 transition-colors hover:bg-slate-200"
                >
                  <Paperclip size={14} /> Dosya Seç
                </button>
                {uploadState[documentTypeId]?.files?.length > 0 && (
                  <p className="text-[10px] font-bold text-slate-500">
                    Seçili dosyalar: {uploadState[documentTypeId].files.length}
                  </p>
                )}
                {documentType.hasExpiryDate && (
                  <input
                    type="date"
                    value={uploadState[documentTypeId]?.expiresAt || ''}
                    onChange={(event) => handleExpiryChange(documentTypeId, event.target.value)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold outline-none"
                  />
                )}
                <button
                  type="button"
                  onClick={() => uploadDocument(documentTypeId)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-white transition-colors hover:bg-slate-800"
                >
                  <Upload size={14} /> Yükle
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {documents.map((document) => (
            <div key={document._id} className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-4 md:flex-row md:items-center md:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                  <FileText size={18} />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-black text-slate-800">{document.fileName || 'Dosya'}</p>
                  <p className="text-[10px] font-bold text-slate-400">
                    {document.fileType || 'Dosya'} • {new Date(document.createdAt).toLocaleDateString('tr-TR')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleDownloadDocument(document._id)}
                  disabled={downloadingDocumentId === document._id}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-50 px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Download size={14} /> {downloadingDocumentId === document._id ? 'İndiriliyor' : 'İndir'}
                </button>
                {row.canDelete && (
                  <button
                    type="button"
                    onClick={() => deleteDocument(document._id)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-50 px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-rose-700 transition-colors hover:bg-rose-100"
                  >
                    <Trash2 size={14} /> Sil
                  </button>
                )}
              </div>
            </div>
          ))}
          {documents.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-xs font-bold text-slate-400">
              Bu evrak tipi için henüz dosya yüklenmedi.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
      <div className="mb-5 flex items-center gap-2">
        <FileText size={20} className="text-blue-600" />
        <h2 className="text-lg font-black text-slate-800">{title}</h2>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm font-black text-slate-400">Yükleniyor...</div>
      ) : (
        <div className="space-y-3">
          {matrix.map(renderMatrixRow)}
          {matrix.length === 0 && (
            <div className="rounded-3xl bg-slate-50 py-10 text-center text-sm font-bold text-slate-400">
              Bu kapsam için tanımlı evrak tipi yok.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentListWidget;
